import { z } from "zod";

const ConfigSchema = z.object({
  isDev: z.boolean(),
  isProd: z.boolean(),
  url: z.string().url(),
  launchDarkly: z.object({
    clientSideId: z.string(),
    devMode: z.boolean().optional(),
  }),
  stripePublicKey: z.string().optional().default(""),
  stripePlatformAccountId: z.string().optional().default(""),
  crispWebsiteId: z.string(),
  frontchatId: z.string(),
  api: z.object({
    url: z.string().url(),
    workerUrl: z.string().url().optional(),
  }),
  meteor: z.object({
    url: z.string().url(),
  }),
  datadog: z
    .object({
      enabled: z.boolean().optional(),
      applicationId: z.string().optional(),
      clientToken: z.string().optional(),
      site: z.string().optional(),
      service: z.string().optional(),
      env: z.string().optional(),
      version: z.string().optional(),
      sessionSampleRate: z.number().optional(),
      sessionReplaySampleRate: z.number().optional(),
      defaultPrivacyLevel: z
        .enum(["allow", "mask-user-input", "mask"])
        .optional(),
    })
    .optional(),
});

type ConfigType = z.infer<typeof ConfigSchema>;

const config: ConfigType = {
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  url: import.meta.env.VITE_URL,
  launchDarkly: {
    clientSideId: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID,
    devMode: import.meta.env.VITE_LAUNCH_DARKLY_DEV_MODE === "true",
  },
  api: {
    url: import.meta.env.VITE_API_URL,
    workerUrl: import.meta.env.VITE_API_WORKER_URL || undefined,
  },
  meteor: {
    url: import.meta.env.VITE_METEOR_URL,
  },
  datadog:
    import.meta.env.VITE_DATADOG_ENABLED === "true" &&
    import.meta.env.VITE_DATADOG_APPLICATION_ID &&
    import.meta.env.VITE_DATADOG_CLIENT_TOKEN
      ? {
          enabled: true,
          applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
          clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
          site: import.meta.env.VITE_DATADOG_SITE || "datadoghq.eu",
          service: import.meta.env.VITE_DATADOG_SERVICE || "baback-office",
          env:
            import.meta.env.VITE_DATADOG_ENV ||
            (import.meta.env.PROD ? "production" : "development"),
          version: __APP_VERSION__,
          sessionSampleRate:
            Number(import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE) || 100,
          sessionReplaySampleRate:
            Number(import.meta.env.VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE) ||
            20,
          defaultPrivacyLevel:
            (import.meta.env.VITE_DATADOG_PRIVACY_LEVEL as
              | "allow"
              | "mask-user-input"
              | "mask") || "mask-user-input",
        }
      : undefined,

  stripePublicKey: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
  stripePlatformAccountId: import.meta.env.VITE_STRIPE_PLATFORM_ACCOUNT_ID ?? "",
  crispWebsiteId: import.meta.env.VITE_CRISP_WEBSITE_ID,
  frontchatId: import.meta.env.VITE_FRONTCHAT_ID,
};
try {
  ConfigSchema.parse(config);
} catch (error) {
  if (error instanceof z.ZodError) {
    console.error("Config validation errors", error);
  }
  throw new Error("Missing config.");
}

export default config;
